import React from 'react';
import { createRoot } from 'react-dom/client';
import ResizeObserver from 'resize-observer-polyfill';
import { GqlResponseError } from 'utils/gql/errors/GqlResponseError';
import { isAxiosError } from 'utils/gql/axios/axiosUtils';
import ErrorMessage from './components/common/error/Error';
import './static/css/normalize.css';
import './static/css/oc.css';
import './static/css/global-animations.css';
import { initSystem, SystemInitParams } from './utils/startupUtils';
import { generateMessage } from './utils/APIError';
import EnterprisePage from '@oc/pages/EnterprisePage';
import { IntlProvider } from 'providers/IntlProvider/IntlProvider';
import { GqlError } from 'utils/gql/gql.types';
import { AxiosError } from 'utils/gql/axios/AxiosClient';
import { appVersionUpdates } from 'utils/appVersionUpdates';
import { BpDesignThemeProvider, createBpDesignTheme } from '@beeline.com/bp-design-system';
const bpDesignTheme = createBpDesignTheme();
window.ResizeObserver = ResizeObserver;

const isDev = process.env.NODE_ENV === 'development';

initSystem()
  .then(({ user, tenant, sessionForCode }: SystemInitParams) => {
    if (!sessionForCode && !isDev) {
      appVersionUpdates.runInterval();
    }
    getRootElement().render(<EnterprisePage initialUser={user} tenant={tenant} />);
  })
  .catch(async (err: GqlResponseError | AxiosError) => {
    localStorage.removeItem('auth:logged-in');
    const status = isAxiosError(err) ? (err.response?.status ?? 0) : ((err as any).status ?? 0); // handle rest & gql status
    const specificError = (err as AxiosError<{ errors?: GqlError[] }>).response?.data.errors?.[0]?.code;

    if (status === 401 || status === 404) {
      const params = new URLSearchParams(window.location.search);
      if (specificError) {
        params.append('error_code', specificError);
      }
      if (window.location.pathname !== '/' && window.location.pathname !== '/public.html') {
        params.append('source', window.location.pathname);
      }

      window.location.href = `/public.html${params.size ? `?${params.toString()}` : ''}`;
      return;
    }

    return getRootElement().render(
      <div>
        <IntlProvider>
          <BpDesignThemeProvider theme={bpDesignTheme}>
            <ErrorMessage message={generateMessage('Could not retrieve user', err)} enableReload />
          </BpDesignThemeProvider>
        </IntlProvider>
      </div>
    );
  });

function getRootElement() {
  const element = document.getElementById('root');
  if (!element) {
    throw new Error(`element with id="root" does not exist`);
  }
  return createRoot(element);
}

// biome-ignore lint/suspicious/noConsole: console error to be catched by Sentry
document.addEventListener('securitypolicyviolation', console.error.bind(console));
